/* The sample trailer player (Round 2 R1, Round 3 R1), shared since the occasion pages (2026-10-14). Until a URL is
   configured the card is a quiet "coming soon" — no play button, no progress bar; setting VITE_SAMPLE_TRAILER_URL
   restores them. The MP4 attaches only on play. On desktop, play opens the full-screen viewer and the 16:9 card never
   changes size (the sample is 9:16); on phones it plays inline, sized to the viewport minus the header. A page may
   render a phone and a desktop copy, CSS-hidden by breakpoint — only the visible one answers the "ksp:play-sample" event. */
import { useEffect, useRef, useState } from "react";
import { HOME, SAMPLE, trailerCaption } from "@/data/homepage";
import { siteTrack } from "@/lib/siteTrack";
import { IcoClose, IcoPlay } from "./icons";
import { useDialogTrap } from "./InviteSheet";

export function TrailerPlayer({ openingLine = "In a world where most men slow down at 80…", length = "1:35" }: { openingLine?: string; length?: string }) {
  const live = !!HOME.sampleTrailerUrl;
  const [playing, setPlaying] = useState(false);                                  // phones: inline playback in the card
  const [viewer, setViewer] = useState<{ trigger: HTMLElement | null } | null>(null);   // desktop: the full-screen viewer + the button to return focus to
  const root = useRef<HTMLDivElement>(null);
  const playBtn = useRef<HTMLButtonElement>(null);
  const cap = trailerCaption(SAMPLE.title, SAMPLE.recordings, SAMPLE.notes, SAMPLE.photos);
  const [tall, setTall] = useState(false);   // phone inline: once the 9:16 size is known the frame turns portrait
  const play = (trigger: HTMLElement | null) => {
    if (!live) return;
    if (root.current && root.current.offsetParent === null) return;              // the copy hidden at this breakpoint stays quiet
    siteTrack("trailer_play", {}, true);
    if (window.matchMedia("(max-width:767px)").matches) setPlaying(true); else setViewer({ trigger: trigger || playBtn.current });
  };
  useEffect(() => { if (!live) return; const h = (e: Event) => play(((e as CustomEvent).detail as HTMLElement | null) || null); document.addEventListener("ksp:play-sample", h); return () => document.removeEventListener("ksp:play-sample", h); });   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={root}>
      <div className={"lp-film" + (playing && tall ? " lp-film-tall" : "")} role="region" aria-label="Sample trailer">
        {playing && live
          ? <video className="lp-film-video" src={HOME.sampleTrailerUrl} poster={SAMPLE.poster} controls autoPlay playsInline preload="metadata" onLoadedMetadata={(e) => setTall(e.currentTarget.videoHeight > e.currentTarget.videoWidth)} onEnded={() => setPlaying(false)}><track kind="captions" srcLang="en" label="English" /></video>
          : live
            ? <button type="button" ref={playBtn} className="lp-film-poster" onClick={(e) => play(e.currentTarget)} aria-label="Play the sample trailer">
                <div className="lp-film-title">{openingLine}</div>
                <span className="lp-film-play"><IcoPlay size={26} /></span>
                <div className="lp-film-bar"><span>0:00</span><div className="lp-film-track"><i /></div><span>{length}</span></div>
              </button>
            : <div className="lp-film-poster lp-film-soon" role="img" aria-label="Sample trailer — coming soon">
                <div className="lp-film-title">{openingLine}</div>
                <div className="lp-film-soon-label">Sample trailer — coming soon</div>
              </div>}
      </div>
      <div className="lp-film-cap"><span>{cap.left.split(" · ")[0]} · <b>{cap.left.split(" · ")[1]}</b></span><span>{cap.right}</span></div>
      {viewer && <TrailerViewer trigger={viewer.trigger} onClose={() => setViewer(null)} />}
    </div>);
}

/* R1 option A: the sample in a full-screen viewer — dark backdrop, a close button, Esc closes, focus returns to the play
   button. Same focus handling as the invite sheet (useDialogTrap); the backdrop closes on click too. */
function TrailerViewer({ trigger, onClose }: { trigger: HTMLElement | null; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useDialogTrap(box, onClose, trigger, "first");
  return (
    <div className="lp-viewer" ref={box} role="dialog" aria-modal="true" aria-label="Sample trailer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <button type="button" className="lp-viewer-close" aria-label="Close" onClick={onClose}><IcoClose /></button>
      <video className="lp-viewer-video" src={HOME.sampleTrailerUrl} poster={SAMPLE.poster} controls autoPlay playsInline preload="metadata"><track kind="captions" srcLang="en" label="English" /></video>
    </div>);
}
